import { Dispatch, SetStateAction, useCallback, useState } from 'react';

/**
 * A useState hook which allows to implement either fully controlled or fully
 * uncontrolled components.
 *
 * TypeScript port of https://www.npmjs.com/package/@quid/react-use-controlled-state
 *
 * @param defaultValue The default/initial value for the state. This
 * value will only be used, if the state is uncontrolled (if `controlledValue`
 * is `undefined`).
 * @param controlledValue If this value is given (not undefined), it fully
 * defines the current state. Thus any value provided as `defaultValue` will be
 * ignored.
 * @param onChange Called on every state change with the new state as the only
 * parameter.
 */
export function useControlledState<S>(
    defaultValue: S,
    controlledValue?: S,
    onChange?: (newValue: S) => void
): [S, Dispatch<SetStateAction<S>>] {
    const [internalState, setInternalState] = useState(defaultValue);
    const setState: Dispatch<SetStateAction<S>> = useCallback(
        actionOrNewState => {
            setInternalState(currentValue => {
                const newValue =
                    typeof actionOrNewState === 'function'
                        ? (actionOrNewState as (p: S) => S)(
                              controlledValue !== undefined ? controlledValue : currentValue
                          )
                        : actionOrNewState;
                if (typeof onChange === 'function') {
                    onChange(newValue);
                }
                return newValue;
            });
        },
        [controlledValue, onChange]
    );
    if (controlledValue !== undefined) {
        return [controlledValue, setState];
    }
    return [internalState, setState];
}
